<script setup lang="ts">
import { computed, ref } from "vue";
import {
  File,
  FileCheck2,
  FileClock,
  FileWarning,
  EyeOff,
  Search,
  Trash2,
  Upload,
} from "lucide-vue-next";
import ArtifactDeleteDialog from "@/components/ArtifactDeleteDialog.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import type { TaskFile, Visibility } from "@/types/models";

const workspace = useWorkspaceStore();
const search = ref("");
const fileInput = ref<HTMLInputElement | null>(null);
const uploadVisibility = ref<Visibility>("Shared");
const uploading = ref(false);
const uploadError = ref("");
const downloadError = ref("");
const downloadingId = ref("");
const fileToDelete = ref<TaskFile | null>(null);
const deleting = ref(false);
const deleteError = ref("");
const acceptedFileTypes = ".pdf,.txt,.csv,.docx,.xlsx,.pptx,.png,.jpg,.jpeg";
const files = computed(() =>
  (workspace.active?.files || []).filter((file) =>
    `${file.name} ${file.context}`
      .toLowerCase()
      .includes(search.value.toLowerCase()),
  ),
);
const stateIcon = {
  Available: FileCheck2,
  Uploading: FileClock,
  "Needs attention": FileWarning,
};

async function download(item: (typeof files.value)[number]) {
  if (item.availabilityState !== "Available" || downloadingId.value) return;
  const projectId = workspace.active?.project.id;
  if (!projectId) return;
  downloadingId.value = item.id;
  downloadError.value = "";
  try {
    await workspace.downloadFile(projectId, item.id);
  } catch (error) {
    downloadError.value =
      error instanceof Error
        ? error.message
        : "The file could not be downloaded. Try again.";
  } finally {
    downloadingId.value = "";
  }
}

async function uploadSelectedFiles(event: Event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.files?.length) return;
  const projectId = workspace.active?.project.id;
  if (!projectId) return;
  uploading.value = true;
  uploadError.value = "";
  try {
    for (const file of [...input.files])
      await workspace.uploadFile({
        projectId,
        parentType: "project",
        visibility: workspace.isInternal ? uploadVisibility.value : "Shared",
        file,
      });
  } catch (error) {
    uploadError.value =
      error instanceof Error
        ? error.message
        : "The file could not be uploaded. Try again.";
  } finally {
    input.value = "";
    uploading.value = false;
  }
}

async function deleteSelectedFile() {
  const projectId = workspace.active?.project.id;
  if (!projectId || !fileToDelete.value) return;
  deleting.value = true;
  deleteError.value = "";
  try {
    await workspace.deleteFile(projectId, fileToDelete.value.id);
    fileToDelete.value = null;
  } catch (error) {
    deleteError.value =
      error instanceof Error
        ? error.message
        : "We could not delete the file. Try again.";
  } finally {
    deleting.value = false;
  }
}
</script>

<template>
  <div class="resource-page">
    <header class="resource-header">
      <div>
        <h2>Project files</h2>
        <p>Find every permitted project and task attachment in one place.</p>
      </div>
    </header>
    <div class="resource-toolbar">
      <div class="resource-search">
        <Search :size="16" /><input
          v-model="search"
          aria-label="Search files"
          placeholder="Search files and source tasks"
        />
      </div>
      <div class="upload-actions">
        <label v-if="workspace.isInternal" class="internal-file-option">
          <input
            type="checkbox"
            :checked="uploadVisibility === 'Internal'"
            @change="
              uploadVisibility = ($event.target as HTMLInputElement).checked
                ? 'Internal'
                : 'Shared'
            "
          />
          Internal
        </label>
        <input
          ref="fileInput"
          class="sr-only"
          type="file"
          multiple
          aria-label="Choose project files to upload"
          :accept="acceptedFileTypes"
          @change="uploadSelectedFiles"
        />
        <button
          v-if="workspace.isInternal"
          class="button button--primary"
          type="button"
          :disabled="uploading"
          @click="fileInput?.click()"
        >
          <Upload :size="16" />
          {{ uploading ? "Uploading…" : "Upload files" }}
        </button>
      </div>
    </div>
    <p v-if="uploadError" class="upload-error" role="alert">
      {{ uploadError }}
    </p>
    <p v-if="downloadError" class="upload-error" role="alert">
      {{ downloadError }}
    </p>
    <section
      class="resource-sheet"
      :class="{ 'resource-sheet--client': !workspace.isInternal }"
    >
      <div class="file-head">
        <span>File</span><span class="file-source">Source</span
        ><span class="file-updated">Updated</span
        ><span class="file-size">Size</span><span>Status</span
        ><span v-if="workspace.canManageProject" class="sr-only">Actions</span>
      </div>
      <div v-for="item in files" :key="item.id" class="file-item">
        <button
          class="file-name"
          type="button"
          :disabled="item.availabilityState !== 'Available' || !!downloadingId"
          :aria-label="
            item.availabilityState === 'Available'
              ? `Download ${item.name}`
              : `${item.name} is ${item.availabilityState.toLowerCase()}`
          "
          @click="download(item)"
        >
          <span class="file-type"><File :size="19" /></span
          ><span
            ><span class="file-title"
              ><strong>{{ item.name }}</strong
              ><EyeOff
                v-if="workspace.isInternal && item.visibility === 'Internal'"
                :size="13"
                aria-label="Internal — visible only to VIPdesk project participants"
                title="Internal — visible only to VIPdesk project participants" /></span
            ><small>{{ item.type }}</small></span
          >
        </button>
        <span class="file-source">{{ item.context }}</span
        ><span class="file-updated"
          ><strong>{{ item.updatedAt }}</strong
          ><small>{{ item.uploadedBy }}</small></span
        ><span class="file-size">{{ item.size }}</span
        ><span
          class="availability-state"
          :class="`availability-state--${item.availabilityState.toLowerCase().replace(' ', '-')}`"
          ><component :is="stateIcon[item.availabilityState]" :size="15" />{{
            downloadingId === item.id ? "Downloading…" : item.availabilityState
          }}</span
        ><button
          v-if="workspace.canManageProject"
          class="file-delete-button"
          type="button"
          :aria-label="`Delete ${item.name}`"
          @click="
            deleteError = '';
            fileToDelete = item;
          "
        >
          <Trash2 :size="16" />
        </button>
      </div>
      <div v-if="!files.length" class="resource-empty">
        <File :size="26" /><strong>{{
          search ? "No files match this search." : "No project files yet."
        }}</strong
        ><span>{{
          search
            ? "Clear the search to review permitted file records."
            : workspace.isInternal
              ? "Upload a file here or attach one to a task."
              : "Project and task files available to you will appear here."
        }}</span>
      </div>
    </section>
    <ArtifactDeleteDialog
      :open="Boolean(fileToDelete)"
      kind="file"
      :name="fileToDelete?.name || ''"
      :deleting="deleting"
      :error="deleteError"
      @cancel="fileToDelete = null"
      @confirm="deleteSelectedFile"
    />
  </div>
</template>

<style scoped>
.resource-page {
  height: 100%;
  overflow: auto;
  padding: 24px 28px 50px;
  background: var(--paper);
}
.resource-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}
.resource-header p {
  margin-top: 4px;
  color: var(--text-soft);
}
.resource-toolbar {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.upload-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.internal-file-option {
  min-height: 36px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-inline: 9px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--sheet);
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}
.internal-file-option input {
  width: 16px;
  height: 16px;
  accent-color: var(--internal);
}
.upload-error {
  max-width: 760px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid var(--danger);
  border-radius: 5px;
  background: var(--danger-soft);
  color: var(--danger);
}
.resource-search {
  width: min(430px, 60vw);
  height: 36px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 10px;
  border: 1px solid var(--rule-strong);
  border-radius: 5px;
  background: white;
  color: var(--text-faint);
}
.resource-search input {
  min-width: 0;
  flex: 1;
  border: 0;
}
.resource-sheet {
  margin-top: 12px;
  overflow: hidden;
  border: 1px solid var(--rule);
  border-radius: 7px;
  background: white;
}
.file-head,
.file-item {
  display: grid;
  grid-template-columns:
    minmax(230px, 1.6fr) minmax(170px, 1.1fr) minmax(120px, 0.8fr)
    70px 110px 36px;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
}
.file-head {
  min-height: 36px;
  border-bottom: 1px solid var(--rule);
  background: var(--sheet-subtle);
  color: var(--text-faint);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.file-item {
  width: 100%;
  min-height: 66px;
  border-bottom: 1px solid var(--rule);
  background: var(--sheet);
  color: var(--text);
  text-align: left;
}
.file-item:hover {
  background: var(--sheet-subtle);
}
.file-item:last-of-type {
  border-bottom: 0;
}
.file-item > span {
  min-width: 0;
}
.file-name {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.file-name:disabled {
  cursor: default;
}
.file-name > span:last-child,
.file-item > span.file-updated {
  display: grid;
}
.file-title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.file-title strong {
  min-width: 0;
  overflow-wrap: anywhere;
}
.file-title svg {
  flex: 0 0 auto;
  color: var(--internal);
}
.file-item small {
  color: var(--text-soft);
}
.file-type {
  width: 35px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 4px;
  color: var(--shared);
  background: var(--shared-soft);
}
.availability-state {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--success);
  font-size: 11px;
}
.availability-state--uploading {
  color: var(--warning);
}
.availability-state--needs-attention {
  color: var(--danger);
}
.file-delete-button {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-soft);
  cursor: pointer;
}
.file-delete-button:hover {
  background: var(--danger-soft);
  color: var(--danger);
}
.resource-empty {
  min-height: 260px;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 7px;
  color: var(--text-soft);
}
.resource-empty strong {
  color: var(--text);
}
@media (max-width: 900px) {
  .file-head,
  .file-item {
    grid-template-columns: minmax(230px, 1.5fr) minmax(150px, 1fr) 110px 36px;
  }
  .file-updated,
  .file-size {
    display: none;
  }
}
@media (max-width: 600px) {
  .resource-page {
    padding: 18px 14px 40px;
  }
  .resource-header {
    align-items: stretch;
    flex-direction: column;
  }
  .resource-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .resource-search {
    width: 100%;
  }
  .file-head {
    display: none;
  }
  .file-item {
    grid-template-columns: minmax(0, 1fr) auto auto;
    padding: 12px;
  }
  .file-source {
    grid-column: 1;
    color: var(--text-soft);
  }
  .availability-state {
    grid-column: 2;
    grid-row: 1 / 3;
  }
  .file-delete-button {
    grid-column: 3;
    grid-row: 1 / 3;
  }
}
</style>
